import axios from "axios";
import isArray from "lodash/isArray";
import isBoolean from "lodash/isBoolean";
import isEmpty from "lodash/isEmpty";
import { useSnackbar } from "notistack";
import { useCallback, useMemo, useState } from "react";
import CustomSnackbar from "../_src_shared/components/CustomSnackbar";
import { getValueFromMappedObject } from "../utils/objects/getValueFromMappedObject";
import { getAuthToken } from "../lib/digitalSizerHttp";
import { bumpLoanWriteSeqForPath } from "../utils/loan/loanWriteSeq";

// Build a multipart FormData from an upload payload without mutating the
// caller's data. The previous implementation `delete`-ed `.file` off each
// metadata object in place, which permanently stripped the binary off the
// React state still held by the upload UI — so any retry (or any subsequent
// upload re-using that array) silently sent zero `uploadFiles` parts and the
// backend rejected it with 422.
//
// Instead we (1) shallow-copy each metadata entry, append the `File` to the
// FormData under "uploadFiles", and (2) JSON-stringify a sanitized payload
// where the `file` reference is replaced with `undefined` (so it's omitted)
// — neither step mutates the input.
/**
 * The message to show when a mutating request fails.
 *
 * A 4xx is an ACTIONABLE client-state condition and the backend already answers
 * it with a sentence worth reading — LOSException renders `{"message": …}`
 * (app/main.py custom_exception_handler), FastAPI's HTTPException renders
 * `{"detail": …}`. "Could not create loan" hides that; the loan-create
 * correspondent gate is the case that forced this (422 "correspondent
 * originator is required to create a loan"), but it applies to every 4xx.
 *
 * 5xx keeps the generic copy on purpose — a server fault's message is a trace,
 * not something a user can act on. An explicit `onFailMsg` still wins, since a
 * caller that set one meant it.
 */
const failMessage = (err, fallback) => {
  const status = err?.response?.status;
  if (!(status >= 400 && status < 500)) return fallback;
  const data = err?.response?.data;
  const detail = typeof data?.detail === "string" ? data.detail : null;
  const message = typeof data?.message === "string" ? data.message : null;
  return message || detail || fallback;
};

//
// `extraFormFields` are scalar parts appended ALONGSIDE `submission` rather than
// nested inside it — the public borrower-submission routes read `t` / `link_id`
// / `consent_file_name` straight off the form, not off the JSON body.
//
// `usePublicFilenames` picks which of the TWO INCOMPATIBLE part-filename
// conventions the backend expects — see the append below. Default (false) is the
// LOS convention; only the public submission routes set it.
const buildUploadFormData = ({
  payload,
  files,
  name,
  extraFormFields,
  usePublicFilenames = false,
}) => {
  const formDataPayload = new FormData();
  const sourceMetadata = (() => {
    if (Array.isArray(files) && files.length > 0) return files;
    if (Array.isArray(payload?.uploadMetadata)) return payload.uploadMetadata;
    return [];
  })();

  let appendedCount = 0;
  let missingIdCount = 0;
  sourceMetadata.forEach((fileObject) => {
    if (fileObject?.file && fileObject.file instanceof File) {
      // Two backends, two incompatible conventions for the part filename —
      // getting this wrong fails SILENTLY on one side and loudly on the other.
      //
      // LOS routes (`files/upload_loan_docs`, `…_with_associations`,
      // `upload_loan_sizer`, checklists, underwriting, borrower_portal) correlate
      // each binary part to its metadata entry BY PART FILENAME === metadata `id`:
      //   `search_list_of_dicts_by_key_value(metadata, 'id', f.filename)`
      //   (lendmarq-os app/models/loan_files.py:40)
      // A miss yields `file = {}` and the whole metadata entry — docGroup,
      // docType, everything — is discarded with a 200 OK. Sending the display
      // filename here made every LOS upload land with `docGroup: null`.
      //
      // The public submission routes (`public/application`,
      // `public/credit_authorization`) do the opposite: they read the part
      // filename directly, so a bare uuid has no extension, fails
      // `check_upload_limits`' allowlist with **HTTP 413**, and never matches
      // `consent_file_name` in `attach_uploads`.
      //
      // Hence the flag. LOS (`id`) is the default because it's the overwhelming
      // majority of callers; the public forms opt in via `usePublicFilenames`.
      const partFilename = usePublicFilenames
        ? fileObject.file_display_name || fileObject.name || fileObject.file.name
        : fileObject.id;
      if (!usePublicFilenames && !fileObject.id) missingIdCount += 1;
      formDataPayload.append("uploadFiles", fileObject.file, partFilename);
      appendedCount += 1;
    }
  });

  if (missingIdCount > 0) {
    // Not fatal — the file still uploads — but its metadata WILL be dropped
    // server-side, since there's no `id` for the backend's lookup to match.
    // Silent on the wire (200 OK, docGroup/docType null), so say it here.
    console.error(
      `[useAxiosHook] ${name || "Upload"}: ${missingIdCount} file(s) had no \`id\` on their metadata entry. ` +
        `The backend correlates each uploaded part to its metadata BY id, so docGroup / docType / ` +
        `every other metadata field on those files will be silently discarded. ` +
        `Give each upload entry a stable \`id\` (uuid) before submitting.`,
    );
  }

  if (sourceMetadata.length > 0 && appendedCount === 0) {
    // Loud failure beats a silent 422. If a caller asked for a multipart
    // upload but no item carried a File reference, the request is
    // guaranteed to fail server-side — surface it here instead.
    console.error(
      `[useAxiosHook] ${name || "Upload"}: ${sourceMetadata.length} metadata item(s) provided but none had a .file reference. ` +
        `Aborting before the request hits the network — this would have produced a 422 \"uploadFiles: Field required\" response. ` +
        `This usually means the upload state was reused after a prior submission stripped the File refs.`,
    );
    throw new Error(
      "Upload aborted: no File references found in upload metadata.",
    );
  }

  const sanitizedPayload = {
    ...payload,
    uploadMetadata: sourceMetadata.map(({ file, ...rest }) => rest),
  };

  formDataPayload.append("submission", JSON.stringify(sanitizedPayload));

  Object.entries(extraFormFields || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      formDataPayload.append(key, String(value));
    }
  });

  return { formDataPayload, sanitizedPayload };
};

export const useAxiosHook = () => {
  // Auth swapped from Stytch to the digital-sizer JWT. The token is read fresh
  // per request via the axios interceptor below (getAuthToken), so there's no
  // reactive session token to track here.
  const session_token = getAuthToken();

  const [axiosPostIsLoading, setAxiosPostIsLoading] = useState(false);
  const [axiosGetIsLoading, setAxiosGetIsLoading] = useState(false);
  const [axiosPutIsLoading, setAxiosPutIsLoading] = useState(false);
  const [axiosDeleteIsLoading, setAxiosDeleteIsLoading] = useState(false);
  const [axiosSSEIsLoading, setAxiosSSEIsLoading] = useState(false);
  const [axiosSSEProgress, setAxiosSSEProgress] = useState(null);

  const [axiosError, setAxiosError] = useState(null);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Hardcoded to the lendmarq-los backend (the dedicated Lendmarq container app,
  // no longer the shared eastview-los). The build-time env var
  // (REACT_APP_BACKEND_BASE_URL) was baking a stale rtl-integration-portal URL
  // into the production bundle, causing 403s on /loportal/* and /underwriting/*.
  // Pinning the base here makes prod independent of the variable-group value.
  const BASE_URL =
    "https://lendmarq-los.bluebay-5ecdbe39.eastus2.azurecontainerapps.io";

  const domainTypeMap = {
    default: BASE_URL,
    // Pipeline (`/loportal/*`, `/underwriting/*`) lives on the same first-party
    // LOS backend as everything else (loan_and_auth_endpoints.md §1). It used to
    // point at a separate resfin-partners integration host during the auth swap;
    // that is retired — keep it aliased to BASE_URL so existing `type:"pipeline"`
    // callers resolve to the documented base.
    pipeline: BASE_URL,
    test: "https://test-integration-portal.wittysmoke-19071c7a.eastus.azurecontainerapps.io",
    errorTesting: "https://env-example.azurewebsites.net",
    resfin:
      "https://rtl-integration-portal.wittysmoke-19071c7a.eastus.azurecontainerapps.io",
  };

  // ----------------------------
  // logger helpers
  // ----------------------------
  const LOG_STYLES = {
    GET: {
      out: "background:#0b8f1a22;color:#0b8f1a;font-weight:600;padding:2px 6px;border-radius:4px;",
      in: "background:#0b8f1a44;color:#0b8f1a;font-weight:600;padding:2px 6px;border-radius:4px;",
      err: "background:#ff000022;color:#b00000;font-weight:600;padding:2px 6px;border-radius:4px;",
    },
    POST: {
      out: "background:#0a66ff22;color:#0a66ff;font-weight:600;padding:2px 6px;border-radius:4px;",
      in: "background:#0a66ff44;color:#0a66ff;font-weight:600;padding:2px 6px;border-radius:4px;",
      err: "background:#ff000022;color:#b00000;font-weight:600;padding:2px 6px;border-radius:4px;",
    },
  };

  const logRequest = ({ method, name, url, payload }) => {
    const m = method.toUpperCase();
    const styles = LOG_STYLES[m] || LOG_STYLES.GET;

    console.groupCollapsed(
      `%c${m} |>| ${name || "(unnamed request)"}`,
      styles.out,
    );
    console.log("URL:", url);
    if (payload !== undefined) console.log("Payload:", payload);
    console.groupEnd();
  };

  const logResponse = ({ method, name, data, isFile }) => {
    const m = method.toUpperCase();
    const styles = LOG_STYLES[m] || LOG_STYLES.GET;

    console.groupCollapsed(
      `%c${m} |<| ${name || "(unnamed request)"}`,
      styles.in,
    );
    console.log("Response:", isFile ? "File request handled" : data);
    console.groupEnd();
  };

  const logError = ({ method, name, err }) => {
    const m = method.toUpperCase();
    const styles = LOG_STYLES[m] || LOG_STYLES.GET;

    console.groupCollapsed(
      `%c${m} |<| ${name || "(unnamed request)"} (ERROR)`,
      styles.err,
    );
    console.error(err);
    console.groupEnd();
  };

  // ----------------------------
  // shared file handler
  // ----------------------------
  const handleFileBlob = ({
    blob,
    headers,
    fileDownload,
    fileDownloadName,
    openInNewTab,
  }) => {
    const fileBlobUrl = window.URL.createObjectURL(blob);

    const contentDisposition = headers?.["content-disposition"];
    let filename = fileDownloadName || "download";

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/["']/g, "");
      }
    }

    if (openInNewTab) {
      const newTab = window.open(fileBlobUrl, "_blank");
      if (!newTab) {
        console.warn("Unable to open new tab; check browser pop-up settings.");
      }
      return;
    }

    if (fileDownload) {
      const a = document.createElement("a");
      a.href = fileBlobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(fileBlobUrl);
    }
  };

  // ----------------------------
  // axios instances per domain
  // ----------------------------
  const axiosClients = useMemo(() => {
    const makeClient = (baseURL) => {
      const instance = axios.create({ baseURL });
      // Inject the (digital-sizer JWT) bearer fresh per request so a login
      // after this hook mounted still authenticates.
      instance.interceptors.request.use((config) => {
        const token = getAuthToken();
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return config;
      });
      return instance;
    };

    return {
      default: makeClient(domainTypeMap.default),
      pipeline: makeClient(domainTypeMap.pipeline),
      resfin: makeClient(domainTypeMap.resfin),
      errorTesting: makeClient(domainTypeMap.errorTesting),
      test: makeClient(domainTypeMap.test),
    };
  }, [session_token]);

  const getClientAndUrl = (typeOrDomain, path) => {
    const client = axiosClients[typeOrDomain] || axiosClients.default;
    const baseURL =
      getValueFromMappedObject(domainTypeMap, typeOrDomain) ||
      domainTypeMap.default;
    const url = `${baseURL}/${path}`;
    return { client, url };
  };

  // -----------------------------------------
  // GET Request
  // -----------------------------------------
  const axiosGetRequest = useCallback(
    async ({
      name,
      type = "default",
      path,
      responseType,
      headers,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
      onFailMsg,
      hideFailMsg,
      cancelSource,

      isFile,
      fileDownload,
      fileDownloadName,
      openInNewTab,
    }) => {
      setAxiosGetIsLoading(true);

      const { client, url } = getClientAndUrl(type, path);
      const effectiveResponseType = isFile ? "blob" : responseType || "json";

      logRequest({ method: "GET", name, url });

      try {
        const response = await client.get(`/${path}`, {
          responseType: effectiveResponseType,
          cancelToken: cancelSource?.token,
          headers,
        });

        const responseData = response.data;

        if (isFile) {
          handleFileBlob({
            blob: responseData,
            headers: response.headers,
            fileDownload,
            fileDownloadName,
            openInNewTab,
          });
        }

        logResponse({ method: "GET", name, data: responseData, isFile });

        if (onSuccessFn) onSuccessFn(responseData, response);
        if (onCompleteFn) onCompleteFn(responseData);
      } catch (err) {
        if (axios.isCancel(err)) {
          console.error("CANCEL", err);
          return;
        }

        setAxiosError(err.message);
        logError({ method: "GET", name, err });

        if (!hideFailMsg) {
          enqueueSnackbar(
            onFailMsg
              ? onFailMsg
              : name
                ? `Could not ${name}`
                : "Could not fetch data",
            {
              autoHideDuration: 2800,
              anchorOrigin: { vertical: "bottom", horizontal: "right" },
              content: (key, message) => (
                <CustomSnackbar
                  id={key}
                  message={message}
                  variant="error"
                  closeSnackbar={() => closeSnackbar(key)}
                />
              ),
            },
          );
        }

        if (onCompleteFn) onCompleteFn();
        if (onFailFn) onFailFn(err);
      } finally {
        setAxiosGetIsLoading(false);
      }
    },
    [axiosClients, session_token],
  );

  // -----------------------------------------
  // POST Request
  // -----------------------------------------
  const axiosPostRequest = useCallback(
    async ({
      name,
      path,
      payload = {},
      files,
      onSuccessFn,
      domain = "default",
      onCompleteFn,
      onSuccessMsg,
      onFailFn,
      onFailMsg,
      hideFailMsg,
      // Status codes whose generic error snackbar should be suppressed (the
      // caller's onFailFn handles them with its own friendly messaging).
      // Non-listed statuses still surface the standard error toast.
      suppressFailStatuses,
      cancelToken,
      headers,
      errorTesting = false,
      includeQueryParams,
      formData = false,
      extraFormFields,
      usePublicFilenames = false,

      isFile,
      fileDownload,
      fileDownloadName,
      openInNewTab,
      responseType,
    }) => {
      setAxiosPostIsLoading(true);

      try {
        if (isBoolean(includeQueryParams)) {
          if (includeQueryParams) {
            const urlParams = new URLSearchParams(window.location.search);
            payload.query_params = Object.fromEntries(urlParams.entries());
          } else {
            payload.query_params = {};
          }
        }

        let dataToSend = payload;
        const finalHeaders = {
          Authorization: `Bearer ${session_token}`,
          ...headers,
        };

        if (formData) {
          const { formDataPayload, sanitizedPayload } = buildUploadFormData({
            payload,
            files,
            name,
            extraFormFields,
            usePublicFilenames,
          });
          dataToSend = formDataPayload;
          finalHeaders["Content-Type"] = "multipart/form-data";
          // Re-bind so logRequest below shows the actual stringified body
          // instead of leaking the raw File refs.
          payload = sanitizedPayload;
        }

        const type = errorTesting ? "errorTesting" : domain;
        const { client, url } = getClientAndUrl(type, path);

        logRequest({ method: "POST", name, url, payload });

        const response = await client.post(`/${path}`, dataToSend, {
          headers: finalHeaders,
          cancelToken,
          responseType: responseType || (isFile ? "blob" : "json"),
        });

        const responseData = response.data;

        if (isFile) {
          handleFileBlob({
            blob: responseData,
            headers: response.headers,
            fileDownload,
            fileDownloadName,
            openInNewTab,
          });
        }

        logResponse({ method: "POST", name, data: responseData, isFile });

        // A write landed. Stamp the loan(s) named in the path so a loan GET
        // that was already in flight can tell its payload is now stale and
        // drop it instead of reverting what was just saved.
        // See utils/loan/loanWriteSeq.js.
        bumpLoanWriteSeqForPath(path);

        if (onSuccessMsg) {
          enqueueSnackbar(onSuccessMsg, {
            autoHideDuration: 2800,
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
            content: (key, message) => (
              <CustomSnackbar
                id={key}
                message={message}
                variant="success"
                closeSnackbar={() => closeSnackbar(key)}
              />
            ),
          });
        }

        if (onSuccessFn) onSuccessFn(responseData);
      } catch (err) {
        setAxiosError(err.response?.data);
        logError({ method: "POST", name, err });

        const failStatusSuppressed =
          Array.isArray(suppressFailStatuses) &&
          suppressFailStatuses.includes(err.response?.status);

        if (!hideFailMsg && !failStatusSuppressed) {
          enqueueSnackbar(
            onFailMsg
              ? onFailMsg
              : failMessage(
                  err,
                  name ? `Could not ${name}` : "Could not submit data",
                ),
            {
              autoHideDuration: 2800,
              anchorOrigin: { vertical: "bottom", horizontal: "right" },
              content: (key, message) => (
                <CustomSnackbar
                  id={key}
                  message={message}
                  variant="error"
                  closeSnackbar={() => closeSnackbar(key)}
                />
              ),
            },
          );
        }

        if (onFailFn) onFailFn(err);
      } finally {
        if (onCompleteFn) onCompleteFn();
        setAxiosPostIsLoading(false);
      }
    },
    [axiosClients, session_token],
  );

  // -----------------------------------------
  // PUT Request
  // -----------------------------------------
  const axiosPutRequest = useCallback(
    async ({
      name,
      path,
      payload = {},
      type = "default",
      headers,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
      onFailMsg,
      hideFailMsg,
      cancelToken,
    }) => {
      setAxiosPutIsLoading(true);

      try {
        const { client, url } = getClientAndUrl(type, path);
        const finalHeaders = {
          Authorization: `Bearer ${session_token}`,
          ...headers,
        };

        logRequest({ method: "POST", name, url, payload });

        const response = await client.put(`/${path}`, payload, {
          headers: finalHeaders,
          cancelToken,
        });

        const responseData = response.data;
        logResponse({ method: "POST", name, data: responseData });

        if (onSuccessMsg) {
          enqueueSnackbar(onSuccessMsg, {
            autoHideDuration: 2800,
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
            content: (key, message) => (
              <CustomSnackbar
                id={key}
                message={message}
                variant="success"
                closeSnackbar={() => closeSnackbar(key)}
              />
            ),
          });
        }

        if (onSuccessFn) onSuccessFn(responseData);
      } catch (err) {
        setAxiosError(err.response?.data);
        logError({ method: "POST", name, err });

        if (!hideFailMsg) {
          enqueueSnackbar(
            onFailMsg
              ? onFailMsg
              : failMessage(
                  err,
                  name ? `Could not ${name}` : "Could not update data",
                ),
            {
              autoHideDuration: 2800,
              anchorOrigin: { vertical: "bottom", horizontal: "right" },
              content: (key, message) => (
                <CustomSnackbar
                  id={key}
                  message={message}
                  variant="error"
                  closeSnackbar={() => closeSnackbar(key)}
                />
              ),
            },
          );
        }

        if (onFailFn) onFailFn(err);
      } finally {
        if (onCompleteFn) onCompleteFn();
        setAxiosPutIsLoading(false);
      }
    },
    [axiosClients, session_token],
  );

  // -----------------------------------------
  // DELETE Request
  // -----------------------------------------
  const axiosDeleteRequest = useCallback(
    async ({
      name,
      path,
      type = "default",
      headers,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
      onFailMsg,
      hideFailMsg,
      cancelToken,
    }) => {
      setAxiosDeleteIsLoading(true);

      try {
        const { client, url } = getClientAndUrl(type, path);
        const finalHeaders = {
          Authorization: `Bearer ${session_token}`,
          ...headers,
        };

        logRequest({ method: "POST", name, url });

        const response = await client.delete(`/${path}`, {
          headers: finalHeaders,
          cancelToken,
        });

        const responseData = response.data;
        logResponse({ method: "POST", name, data: responseData });

        if (onSuccessMsg) {
          enqueueSnackbar(onSuccessMsg, {
            autoHideDuration: 2800,
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
            content: (key, message) => (
              <CustomSnackbar
                id={key}
                message={message}
                variant="success"
                closeSnackbar={() => closeSnackbar(key)}
              />
            ),
          });
        }

        if (onSuccessFn) onSuccessFn(responseData);
      } catch (err) {
        setAxiosError(err.response?.data);
        logError({ method: "POST", name, err });

        if (!hideFailMsg) {
          enqueueSnackbar(
            onFailMsg
              ? onFailMsg
              : failMessage(
                  err,
                  name ? `Could not ${name}` : "Could not delete data",
                ),
            {
              autoHideDuration: 2800,
              anchorOrigin: { vertical: "bottom", horizontal: "right" },
              content: (key, message) => (
                <CustomSnackbar
                  id={key}
                  message={message}
                  variant="error"
                  closeSnackbar={() => closeSnackbar(key)}
                />
              ),
            },
          );
        }

        if (onFailFn) onFailFn(err);
      } finally {
        if (onCompleteFn) onCompleteFn();
        setAxiosDeleteIsLoading(false);
      }
    },
    [axiosClients, session_token],
  );

  // -----------------------------------------
  // SSE Request (POST to start → stream progress → GET result)
  // -----------------------------------------
  const axiosSSERequest = useCallback(
    async ({
      name,
      path,
      payload = {},
      domain = "default",
      headers,
      formData = false,
      files,

      // SSE-specific
      // bearerToken,
      // bearerToken = "69b97e6137782569c074f7aa-foosball",
      onProgressFn,
      getSSEUrl, // (responseData) => string — extract SSE URL from POST response
      getStatusUrl, // (responseData) => string — extract final result URL (optional)

      // Standard callbacks
      onSuccessFn,
      onCompleteFn,
      onProcessCompleteFn,
      onFailFn,
      onFailMsg,
      hideFailMsg,
    }) => {
      setAxiosSSEIsLoading(true);
      setAxiosSSEProgress(null);

      // Used to signal abort to the fetch-based SSE reader
      let sseAbortController = null;

      try {
        // ── Phase 1: POST to kick off the workflow ──
        const effectiveToken = session_token;
        // const effectiveToken = bearerToken || session_token;
        let dataToSend = payload;
        const finalHeaders = {
          Authorization: `Bearer ${effectiveToken}`,
          ...headers,
        };

        if (formData) {
          const { formDataPayload, sanitizedPayload } = buildUploadFormData({
            payload,
            files,
            name: `${name} (SSE start)`,
          });
          dataToSend = formDataPayload;
          finalHeaders["Content-Type"] = "multipart/form-data";
          payload = sanitizedPayload;
        }

        const { client, url } = getClientAndUrl(domain, path);

        logRequest({
          method: "POST",
          name: `${name} (SSE start)`,
          url,
          payload,
        });

        const response = await client.post(`/${path}`, dataToSend, {
          headers: finalHeaders,
        });

        const responseData = response.data;

        logResponse({
          method: "POST",
          name: `${name} (SSE start)`,
          data: responseData,
        });

        // ── Phase 2: Subscribe to SSE via fetch (supports Authorization header) ──
        const sseUrl = getSSEUrl
          ? getSSEUrl(responseData)
          : responseData?.sse_url;

        if (!sseUrl) {
          throw new Error("No SSE URL returned from initial POST");
        }

        const baseURL =
          getValueFromMappedObject(domainTypeMap, domain) ||
          domainTypeMap.default;

        const fullSSEUrl = sseUrl.startsWith("http")
          ? sseUrl
          : `${baseURL}${sseUrl.startsWith("/") ? "" : "/"}${sseUrl}`;

        console.groupCollapsed(
          `%cSSE |>| ${name || "(unnamed)"}`,
          LOG_STYLES.GET.out,
        );
        console.log("SSE URL:", fullSSEUrl);
        console.groupEnd();

        // Use fetch instead of EventSource so we can send the Authorization header
        // (EventSource does not support custom headers)
        sseAbortController = new AbortController();

        const sseResponse = await fetch(fullSSEUrl, {
          headers: {
            Authorization: `Bearer ${effectiveToken}`,
            Accept: "text/event-stream",
          },
          signal: sseAbortController.signal,
        });

        if (!sseResponse.ok) {
          throw new Error(
            `SSE connection failed: ${sseResponse.status} ${sseResponse.statusText}`,
          );
        }

        const reader = sseResponse.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        const processSSEEvents = async () => {
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              console.groupCollapsed(
                `%cSSE |<| ${name || "(unnamed)"} (STREAM ENDED)`,
                LOG_STYLES.GET.in,
              );
              console.log("SSE stream closed by server");
              console.groupEnd();
              break;
            }

            buffer += decoder.decode(value, { stream: true });

            // Parse SSE frames from buffer
            const lines = buffer.split("\n");
            buffer = lines.pop() || ""; // Keep incomplete line in buffer

            for (const line of lines) {
              // Skip heartbeat comments and empty lines
              if (line.startsWith(":") || line.trim() === "") continue;

              if (line.startsWith("data:")) {
                const dataStr = line.slice(5).trim();
                if (!dataStr) continue;

                try {
                  const progress = JSON.parse(dataStr);

                  setAxiosSSEProgress(progress);
                  if (onProgressFn) onProgressFn(progress);

                  // ── Error from SSE ──
                  if (progress.error) {
                    if (sseAbortController) sseAbortController.abort();

                    logError({
                      method: "GET",
                      name: `${name} (SSE)`,
                      err: new Error(progress.error),
                    });

                    if (!hideFailMsg) {
                      enqueueSnackbar(
                        onFailMsg || progress.error || `${name} failed`,
                        {
                          autoHideDuration: 2800,
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "right",
                          },
                          content: (key, message) => (
                            <CustomSnackbar
                              id={key}
                              message={message}
                              variant="error"
                              closeSnackbar={() => closeSnackbar(key)}
                            />
                          ),
                        },
                      );
                    }

                    if (onFailFn) onFailFn(new Error(progress.error));
                    if (onCompleteFn) onCompleteFn();
                    setAxiosSSEIsLoading(false);
                    return;
                  }

                  // ── Complete ──
                  if (progress.complete) {
                    if (sseAbortController) sseAbortController.abort();

                    console.groupCollapsed(
                      `%cSSE |<| ${name || "(unnamed)"} (COMPLETE)`,
                      LOG_STYLES.GET.in,
                    );
                    console.log("Final SSE payload:", progress);
                    console.groupEnd();

                    if (onProcessCompleteFn) {
                      onProcessCompleteFn(progress);
                    }

                    return;
                  }
                } catch (parseErr) {
                  console.error("SSE parse error:", parseErr, dataStr);
                }
              }
            }
          }
        };

        await processSSEEvents();
      } catch (err) {
        // Ignore abort errors (we triggered them intentionally on complete/error)
        if (err.name === "AbortError") return;

        // Phase 1 POST failed or SSE fetch failed
        setAxiosError(err.response?.data || err.message);
        logError({ method: "POST", name: `${name} (SSE start)`, err });

        if (!hideFailMsg) {
          enqueueSnackbar(
            onFailMsg || `Could not start ${name || "SSE workflow"}`,
            {
              autoHideDuration: 2800,
              anchorOrigin: { vertical: "bottom", horizontal: "right" },
              content: (key, message) => (
                <CustomSnackbar
                  id={key}
                  message={message}
                  variant="error"
                  closeSnackbar={() => closeSnackbar(key)}
                />
              ),
            },
          );
        }

        if (onFailFn) onFailFn(err);
        if (onCompleteFn) onCompleteFn();
        setAxiosSSEIsLoading(false);
      }
    },
    [axiosClients, axiosGetRequest, session_token],
  );

  return {
    axiosPostIsLoading,
    axiosGetIsLoading,
    axiosPutIsLoading,
    axiosDeleteIsLoading,
    axiosSSEIsLoading,
    axiosSSEProgress,
    axiosError,
    axiosGetRequest,
    axiosPostRequest,
    axiosPutRequest,
    axiosDeleteRequest,
    axiosSSERequest,
  };
};
